import React from 'react';
import { Badge, Box, Tab, Tabs, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import './archive-form.scss';

/** 工作台内可导航的建档分区。 */
export interface ArchiveWorkbenchSection {
  /** 稳定分区标识。 */
  key: string;
  /** 已翻译标题。 */
  title: string;
  /** 已翻译来源文案。 */
  sourceLabel: string;
  /** 剩余必填数。 */
  pendingRequired: number;
  /** 分区内容。 */
  content: React.ReactNode;
}

/** 建档分区锚点导航属性。 */
export interface ArchiveSectionAnchorNavProps {
  /** 当前高亮分区。 */
  activeKey: string;
  /** 导航项。 */
  sections: ReadonlyArray<Pick<ArchiveWorkbenchSection, 'key' | 'title' | 'pendingRequired'>>;
  /** 用户点击导航项。 */
  onSectionChange: (key: string) => void;
}

/**
 * 展示可横向滚动的建档分区导航及剩余必填数量。
 * @param props - 当前分区、导航项和切换回调
 */
export function ArchiveSectionAnchorNav({
  activeKey,
  sections,
  onSectionChange,
}: ArchiveSectionAnchorNavProps) {
  /** 当前界面的翻译函数，用于无障碍标签。 */
  const { t } = useTranslation();
  /**
   * 将 MUI Tab 的选中值转交给工作台。
   * @param _event - MUI 触发的切换事件
   * @param nextKey - 新选中的分区标识
   */
  const handleChange = (_event: React.SyntheticEvent, nextKey: string) => {
    onSectionChange(nextKey);
  };

  return (
    <Tabs
      aria-label={t('archive_form_section_navigation')}
      className="archive-form__anchor-nav"
      value={activeKey}
      onChange={handleChange}
      variant="scrollable"
      scrollButtons="auto"
    >
      {sections.map((section) => {
        // 分区完成时以勾选标记替代数字角标
        const badgeContent = section.pendingRequired === 0 ? '✓' : section.pendingRequired;

        return (
          <Tab
            key={section.key}
            value={section.key}
            label={(
              <Badge
                badgeContent={badgeContent}
                className={section.pendingRequired === 0 ? 'archive-form__badge--complete' : undefined}
              >
                <Box className="archive-form__tab-label">
                  <Typography variant="body2">{section.title}</Typography>
                </Box>
              </Badge>
            )}
          />
        );
      })}
    </Tabs>
  );
}
